import {
  Navigate,
  Route,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom";
import * as Sentry from "@sentry/react";

import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/dropzone/styles.css";
import "mantine-datatable/styles.css";
import "@mantine/tiptap/styles.css";
import "@mantine/charts/styles.css";
import "./App.css";
import AnonRoute from "./components/AnonRoute";
import Login from "./components/Auth/Login";
import Register from "./components/Auth/Register";
import WorkspaceInviteAccept from "./components/Auth/WorkspaceInviteAccept";
import EditProfile from "./components/EditProfile";
import CreateEvent from "./components/Events/CreateEvent";
import EditEvent from "./components/Events/Edit";
import GuardRoute from "./components/GuardRoute";
import CreatePolls from "./components/Polls/CreatePolls";
import EditPolls from "./components/Polls/EditPolls";
import PollResults from "./components/Polls/PollResult";
import Profile from "./components/Profile";
import CreateSession from "./components/Sessions/CreateSession";
import EditSession from "./components/Sessions/Edit";
import CreateSponsor from "./components/Sponsors/CreateSponsor";
import EditSponsor from "./components/Sponsors/Edit";
import CreateUser from "./components/Users/CreateUser";
import EditUser from "./components/Users/EditUser";
import CreateWorkSpace from "./components/Workspaces/CreateWorkSpace";
import EditWorkspace from "./components/Workspaces/Edit";
import AppShell from "./pages/AppShell";
import Callback from "./pages/Callback";
import NotFound from "./pages/NotFound";
import Polls from "./pages/Polls";
import PollsSessions from "./pages/PollsSessions";
import Sessions from "./pages/Sessions";
import Sponsors from "./pages/Sponsors";
import Users from "./pages/Users";
import Pages from "./pages/Pages";
import CreatePage from "./components/Pages/CreatePage";
import EditPage from "./components/Pages/EditPage";
import Announcements from "./pages/Announcements";
import Notifications from "./pages/Notifications";
import CreateAnnouncement from "./components/Announcements/CreateAnnouncement";
import EditAnnouncement from "./components/Announcements/EditAnnouncement";
import ResetPassword from "./components/Auth/ResetPassword";
import GoogleSignIn from "./components/Auth/GoogleSignIn";
import Tags from "./pages/Tags";
import CreateTag from "./components/Tags/CreateTag";
import ColorTag from "./components/Tags/ColorTags";
import EditTag from "./components/Tags/Edit";
import Tickets from "./pages/Tickets";
import TicketsPreview from "./components/TicketsPreview/TicketsPreview";
import CreateTicket from "./components/Tickets/Create";
import EditTicket from "./components/Tickets/Edit";
import SysAdminRoute from "./components/SysAdminRoute";
import FeatureFlagRoute from "./components/FeatureFlagRoute";
import SystemSettingRoute from "./components/SystemSettingRoute";
import { FEATURE_FLAGS } from "@congrs/schemas";
import SpecialInvitation from "./pages/SpecialInvitation";
import CreateSpecialInvite from "./components/SpecialInvite/Create";
import Speakers from "./pages/Speakers";
import Create from "./components/Speakers/Create";
import EditSpeaker from "./components/Speakers/Edit";
import ImportSpeakers from "./components/Speakers/Import";
import SponsorLevels from "./components/Sponsors/SponsorLevels";
import PublicSessionsTable from "./components/Sessions/PublicSessionsTable";
import PublicSessions from "./pages/PublicSessions";
import PublicEventView from "./pages/PublicEventView";
import RegistrationsOverview from "./pages/RegistrationsOverview";
import RegistrationForm from "./pages/RegistrationForm";
import InviteAttendees from "./pages/InviteAttendees";
import SystemSettings from "./pages/SystemSettings";
import EditSystemSettings from "./components/SystemSettings/Edit";
import Surveys from "./pages/Surveys";
import SurveyQuestions from "./pages/SurveyQuestions";
import CreateQuestion from "./components/Surveys/CreateQuestion";
import EditQuestion from "./components/Surveys/EditQuestion";
import SurveyResponses from "./pages/SurveyResponses";
import CustomTabsEvent from "./components/Events/Customize/CustomTabsEvent";
import Locations from "./components/Events/Customize/Locations";
import CheckIn from "./pages/CheckIn";
import CheckInPrint from "./pages/CheckInPrint";
import EventRegistration from "./pages/EventRegistration";
import Languages from "./pages/Languages";
import Giveaway from "./pages/Giveaway";
import PublicPollResults from "./pages/PublicPollResults";
import I18nProvider from "./components/i18nProvider";
import Open from "./pages/Open";
import OpenDocument from "./pages/OpenDocument";
import OpenTicket from "./pages/OpenTicket";
import WorkspaceDashboard from "./pages/WorkspaceDashboard";
import CreateTemplate from "./components/DocumentGenerators/CreateTemplate";
import EditDocumentGenerator from "./pages/EditDocumentGenerator";
import Campaigns from "./pages/Campaigns";
import Documents from "./pages/Documents";
import DocumentFileNames from "./pages/DocumentFileNames";
import DocumentsByFileName from "./pages/DocumentsByFileName";
import DocumentEmails from "./pages/DocumentEmails";
import BatchUploadWizard from "./components/UserDocuments/BatchUploadWizard";
import Badges from "./pages/Badges";
import EditBadge from "./pages/EditBadge";
import CreateCampaign from "./components/Campaigns/CreateCampaign";
import EditCampaign from "./components/Campaigns/EditCampaign";
import CampaignLogs from "./components/Campaigns/CampaignLogs";
import ViewCampaign from "./components/Campaigns/ViewCampaign";
import TemplatePreviews from "./pages/TemplatePreviews";
import TemplatePreviewDetail from "./pages/TemplatePreviewDetail";
import Insights from "./pages/Insights";
import AppUsageWeights from "./pages/AppUsageWeights";
import Compliance from "./pages/Compliance";
import Setup from "./pages/Setup";

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

const App = sentryCreateBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="/open/claim/:code" element={<OpenTicket />} />
      <Route path="/join" element={<OpenTicket />} />
      <Route path="/join/:code" element={<OpenTicket />} />
      <Route path="/open/*" element={<Open />} />
      <Route element={<I18nProvider />}>
        <Route
          path="public/poll-results/:shareToken"
          element={<PublicPollResults />}
        />
        <Route path="public-session/:slug" element={<PublicSessions />} />
        <Route path="public-event/:slug" element={<PublicEventView />} />
        <Route
          path="workspace-invite/:token"
          element={<WorkspaceInviteAccept />}
        />
        <Route path="events/:slug/register" element={<EventRegistration />} />
        <Route path="open/documents/:token" element={<OpenDocument />} />
      </Route>
      <Route path="/" element={<AnonRoute />}>
        <Route path="login" element={<Login />} />
        <Route path="invite/:token" element={<Register />} />
        <Route path="forgot-password/:token" element={<ResetPassword />} />
        <Route path="google-sign-in" element={<GoogleSignIn />} />
      </Route>
      <Route element={<GuardRoute />}>
        <Route
          path="/app/polls/:id/results/present"
          element={<PollResults />}
        />
        <Route
          path="app"
          element={<AppShell />}
          handle={{
            name: "Organization",
          }}
        >
          <Route element={<WorkspaceDashboard />} index />

          <Route
            path="events"
            handle={{
              name: "Events",
            }}
          >
            <Route index element={<Navigate to="/app" replace />} />
            <Route
              path="create"
              element={<CreateEvent />}
              handle={{ name: "Create" }}
            />
            <Route element={<SysAdminRoute />}>
              <Route
                path=":id"
                element={<EditEvent />}
                handle={{ name: "Edit" }}
              />
            </Route>
            <Route handle={{ requiresEvent: true }}>
              <Route
                path="settings"
                element={<CustomTabsEvent />}
                handle={{ name: "Settings" }}
              />
              <Route
                path="customize"
                element={<Navigate to="/app/events/settings" replace />}
              />
            </Route>
          </Route>
          <Route path="profile" handle={{ name: "Profile" }}>
            <Route index element={<Profile />} />
            <Route
              path="edit"
              element={<EditProfile />}
              handle={{
                name: "Edit",
              }}
            />
          </Route>

          <Route path="workspaces" handle={{ name: "Workspaces" }}>
            <Route index element={<Navigate to="/app" replace />} />
            <Route
              path="create"
              element={<CreateWorkSpace />}
              handle={{ name: "Create" }}
            />
            <Route
              path=":id"
              element={<EditWorkspace />}
              handle={{ name: "Edit" }}
            />
          </Route>
          <Route handle={{ requiresEvent: true }}>
            <Route
              element={<SystemSettingRoute requires="enableSetupScreen" />}
            >
              <Route
                path="setup"
                element={<Setup />}
                handle={{ name: "Setup" }}
              />
            </Route>
            <Route
              path="sessions"
              handle={{
                name: "Sessions",
              }}
            >
              <Route index element={<Sessions />} />
              <Route
                path="create"
                element={<CreateSession />}
                handle={{ name: "Create" }}
              />
              <Route
                path=":id"
                element={<EditSession />}
                handle={{ name: "Edit" }}
              />
              <Route
                path="public"
                element={<PublicSessionsTable />}
                handle={{
                  name: "Public Sessions",
                }}
              />
              <Route
                path="locations"
                element={<Locations />}
                handle={{
                  name: "Locations",
                }}
              />
            </Route>
            <Route path="/app/campaigns" handle={{ name: "Campaigns" }}>
              <Route index element={<Campaigns />} />
              <Route
                path="create"
                element={<CreateCampaign />}
                handle={{ name: "Create" }}
              />
              <Route
                path=":id/view"
                element={<ViewCampaign />}
                handle={{ name: "Preview" }}
              />
              <Route
                path=":id/edit"
                element={<EditCampaign />}
                handle={{ name: "Edit" }}
              />
              <Route
                path=":id/logs"
                element={<CampaignLogs />}
                handle={{ name: "Logs" }}
              />
            </Route>

            <Route
              element={
                <FeatureFlagRoute flag={FEATURE_FLAGS.EVENT_COMPLIANCE} />
              }
            >
              <Route path="/app/compliance" handle={{ name: "Compliance" }}>
                <Route index element={<Compliance />} />
              </Route>
            </Route>

            <Route
              element={<FeatureFlagRoute flag={FEATURE_FLAGS.DOCUMENTS} />}
            >
              <Route path="/app/documents" handle={{ name: "Documents" }}>
                <Route element={<Documents />}>
                  <Route index element={<DocumentFileNames />} />
                  <Route
                    path=":distributionId"
                    element={<DocumentsByFileName />}
                    handle={{ name: "Documents" }}
                  />
                  <Route
                    path=":distributionId/emails"
                    element={<DocumentEmails />}
                    handle={{ name: "Emails" }}
                  />
                </Route>
                <Route
                  path="batch-upload"
                  element={<BatchUploadWizard />}
                  handle={{ name: "Upload" }}
                />
              </Route>
              <Route
                path="/app/documents/document-templates"
                handle={{ name: "Templates" }}
              >
                <Route
                  index
                  element={
                    <Navigate to="/app/documents?tab=templates" replace />
                  }
                />
                <Route
                  path="create"
                  element={<CreateTemplate />}
                  handle={{ name: "Create" }}
                />
                <Route
                  path=":id"
                  element={<EditDocumentGenerator />}
                  handle={{ name: "Edit" }}
                />
              </Route>
            </Route>

            <Route element={<FeatureFlagRoute flag={FEATURE_FLAGS.BADGES} />}>
              <Route path="/app/badges" handle={{ name: "Badges" }}>
                <Route index element={<Badges />} />
                <Route
                  path=":id"
                  element={<EditBadge />}
                  handle={{ name: "Edit" }}
                />
              </Route>
            </Route>

            <Route element={<FeatureFlagRoute flag={FEATURE_FLAGS.POLLS} />}>
              <Route path="/app/polls" handle={{ name: "Polls" }}>
                <Route index element={<PollsSessions />} />
                <Route
                  path="create"
                  element={<CreatePolls />}
                  handle={{ name: "Create" }}
                />
                <Route path="/app/polls/:sessionId" element={<Polls />} />
                <Route
                  path="/app/polls/:id/edit"
                  element={<EditPolls />}
                  handle={{ name: "Edit" }}
                />
                <Route
                  path="/app/polls/:id/results"
                  element={<PollResults />}
                  handle={{ name: "Results" }}
                />
              </Route>
            </Route>

            <Route element={<FeatureFlagRoute flag={FEATURE_FLAGS.SURVEYS} />}>
              <Route path="/app/surveys" handle={{ name: "Surveys" }}>
                <Route index element={<Surveys />} />
                <Route
                  path=":id/questions"
                  element={<SurveyQuestions />}
                  handle={{ name: "Questions" }}
                />
                <Route
                  path=":id/questions/create"
                  element={<CreateQuestion />}
                  handle={{ name: "Create" }}
                />
                <Route
                  path=":id/questions/edit/:questionId"
                  element={<EditQuestion />}
                  handle={{ name: "Edit Question" }}
                />
                <Route
                  path=":id/responses"
                  element={<SurveyResponses />}
                  handle={{ name: "Responses" }}
                />
              </Route>
            </Route>

            <Route
              element={<FeatureFlagRoute flag={FEATURE_FLAGS.GIVEAWAYS} />}
            >
              <Route path="/app/giveaway" handle={{ name: "Giveaway" }}>
                <Route index element={<Giveaway />} />
              </Route>
            </Route>

            <Route path="/app/insights" handle={{ name: "Insights" }}>
              <Route index element={<Insights />} />
              <Route element={<SysAdminRoute />}>
                <Route
                  path="app-usage-weights"
                  element={<AppUsageWeights />}
                  handle={{ name: "App feature usage weights" }}
                />
              </Route>
            </Route>

            <Route
              path="sponsors"
              handle={{
                name: "Sponsors",
              }}
            >
              <Route index element={<Sponsors />} />
              <Route
                path="create"
                element={<CreateSponsor />}
                handle={{ name: "Create" }}
              />
              <Route
                path=":id"
                element={<EditSponsor />}
                handle={{ name: "Edit" }}
              />
              <Route
                path="levels"
                element={<SponsorLevels />}
                handle={{ name: "Levels" }}
              />
            </Route>
            <Route
              path="pages"
              handle={{
                name: "Pages",
              }}
            >
              <Route index element={<Pages />} />
              <Route
                path="create"
                element={<CreatePage />}
                handle={{ name: "Create" }}
              />
              <Route
                path=":id"
                element={<EditPage />}
                handle={{ name: "Edit" }}
              />
            </Route>
            <Route
              path="announcements"
              handle={{
                name: "Announcements",
              }}
            >
              <Route index element={<Announcements />} />
              <Route
                path="create"
                element={<CreateAnnouncement />}
                handle={{ name: "Create" }}
              />
              <Route
                path=":announcementId"
                element={<EditAnnouncement />}
                handle={{ name: "Edit" }}
              />
            </Route>
            <Route
              path="notifications"
              element={<Notifications />}
              handle={{ name: "Notifications" }}
            />
            <Route
              path="special-invitation"
              handle={{
                name: "Special Invitation",
              }}
            >
              <Route index element={<SpecialInvitation />} />
              <Route
                path="create"
                element={<CreateSpecialInvite />}
                handle={{ name: "Create" }}
              />
            </Route>
            <Route
              path="tags"
              handle={{
                name: "Tags",
              }}
            >
              <Route index element={<Tags />} />
              <Route
                path="create"
                element={<CreateTag />}
                handle={{ name: "Create" }}
              />
              <Route
                path="colorTags"
                element={<ColorTag />}
                handle={{ name: "Colors" }}
              />
              <Route
                path=":tagId"
                element={<EditTag />}
                handle={{ name: "Edit" }}
              />
            </Route>
            <Route
              path="tickets-preview"
              element={<TicketsPreview />}
              handle={{ name: "Tickets (preview)" }}
            />
            <Route path="tickets" handle={{ name: "Tickets" }}>
              <Route index element={<Tickets />} />
              <Route
                path="create"
                element={<CreateTicket />}
                handle={{ name: "Create" }}
              />
              <Route
                path=":ticketId"
                element={<EditTicket />}
                handle={{ name: "Edit" }}
              />
            </Route>

            <Route element={<FeatureFlagRoute flag={FEATURE_FLAGS.CHECK_IN} />}>
              <Route path="check-in" handle={{ name: "Check-in" }}>
                <Route index element={<CheckIn />} />
                <Route
                  path="print/:code"
                  element={<CheckInPrint />}
                  handle={{ name: "Print station" }}
                />
              </Route>
            </Route>

            <Route path="speakers" handle={{ name: "Speakers" }}>
              <Route index element={<Speakers />} />
              <Route
                path="create"
                element={<Create />}
                handle={{ name: "Create" }}
              />
              <Route
                path=":speakerId"
                element={<EditSpeaker />}
                handle={{ name: "Edit" }}
              />
              <Route
                path="import"
                element={<ImportSpeakers />}
                handle={{ name: "Import" }}
              />
            </Route>
            <Route path="registrations" handle={{ name: "Registrations" }}>
              <Route
                element={
                  <FeatureFlagRoute flag={FEATURE_FLAGS.INVITE_EMAILS} />
                }
              >
                <Route
                  path="invite-attendees"
                  element={<InviteAttendees />}
                  handle={{ name: "Invite Attendees" }}
                />
              </Route>
              <Route
                element={
                  <FeatureFlagRoute flag={FEATURE_FLAGS.REGISTRATIONS} />
                }
              >
                <Route index element={<Navigate to="overview" replace />} />
                <Route
                  path="form"
                  element={<RegistrationForm />}
                  handle={{ name: "Registration Form" }}
                />
                <Route
                  path="overview"
                  element={<RegistrationsOverview />}
                  handle={{ name: "Overview" }}
                />
              </Route>
            </Route>
          </Route>

          <Route element={<SysAdminRoute />}>
            <Route
              path="system-settings"
              element={<SystemSettings />}
              handle={{ name: "Global System Configuration" }}
            />
            <Route
              path="system-settings/edit"
              element={<EditSystemSettings />}
              handle={{ name: "Edit Global System Configuration" }}
            />

            <Route
              path="users"
              handle={{
                name: "Users",
              }}
            >
              <Route index element={<Users />} />
              <Route
                path="create"
                element={<CreateUser />}
                handle={{
                  name: "Create",
                }}
              />
              <Route
                path=":id"
                element={<EditUser />}
                handle={{
                  name: "Edit",
                }}
              />
            </Route>

            <Route path="languages" handle={{ name: "Languages" }}>
              <Route index element={<Languages />} />
            </Route>
            <Route
              path="translations/ui"
              element={
                <Navigate to="/app/languages?tab=translations" replace />
              }
            />

            <Route
              path="template-previews"
              handle={{ name: "Template Previews" }}
            >
              <Route index element={<TemplatePreviews />} />
              <Route
                path=":templateName"
                element={<TemplatePreviewDetail />}
                handle={{ name: "Preview" }}
              />
            </Route>
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<NotFound />} />
      <Route path="/callback" element={<Callback />} />
    </>
  )
);

export default App;
